import React, { useState } from "react";
import emailjs from "emailjs-com";
import styled, { keyframes } from "styled-components";
import { FaPaperPlane } from "react-icons/fa";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const ContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1200px;
  margin: 2rem auto;
  padding: 1rem;
  box-shadow: 0 10px 30px rgba(72, 50, 133, 0.1);
  border-radius: 20px;
  background: white;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out forwards;

  @media (min-width: 768px) {
    flex-direction: row;
    min-height: 600px;
  }
`;

const FormContainer = styled.div`
  padding: 2rem;
  width: 100%;
  background: linear-gradient(135deg, #f9f9ff 0%, #f0f0ff 100%);
  
  @media (min-width: 768px) {
    width: 50%;
    padding: 3rem;
  }
`;

const ImageContainer = styled.div`
  display: none;
  position: relative;
  overflow: hidden;
  
  @media (min-width: 768px) {
    display: flex;
    width: 50%;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg,rgb(22, 26, 141) 0%,rgb(9, 24, 107) 100%);
  }
`;

const ContactImage = styled.img`
  width: 80%;
  max-width: 400px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.2));
  transition: transform 0.5s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const Heading = styled.h1`
  font-size: 2rem;
  color: #483285;
  margin-bottom: 1.5rem;
  position: relative;
  font-weight: 700;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(90deg, #8a63d2 0%, #483285 100%);
    border-radius: 2px;
  }

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  position: absolute;
  top: -10px;
  left: 15px;
  background: white;
  padding: 0 5px;
  font-size: 0.9rem;
  color: #483285;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px;
  border: 2px solid #e0e0ff;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  
  &:focus {
    border-color: #8a63d2;
    box-shadow: 0 0 0 3px rgba(138, 99, 210, 0.2);
    outline: none;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 15px;
  border: 2px solid #e0e0ff;
  border-radius: 10px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;
  background: white;
  
  &:focus {
    border-color: #8a63d2;
    box-shadow: 0 0 0 3px rgba(138, 99, 210, 0.2);
    outline: none;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px;
  background: linear-gradient(135deg, #8a63d2 0%, #483285 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(72, 50, 133, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(72, 50, 133, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    transition: transform 0.5s ease;
  }
  
  &:hover svg {
    animation: ${pulse} 1s infinite;
  }
`;

const SuccessMessage = styled.div`
  padding: 15px;
  background: #4caf50;
  color: white;
  border-radius: 10px;
  text-align: center;
  margin-top: 1rem;
  font-weight: 500;
  animation: ${fadeIn} 0.5s ease-out;
`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    emailjs
      .send(
        "service_xb9r30c",
        "template_8nv3omd",
        formData,
        "xKNMmzxPU3tqOBjLg"
      )
      .then((response) => {
        console.log("Email sent successfully:", response.status, response.text);
        setFormData({
          name: "",
          email: "",
          message: "",
        });
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 3000);
      })
      .catch((err) => {
        console.error("Failed to send email:", err);
        alert("Failed to send message. Please try again later.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <ContactContainer>
      <FormContainer>
        <Heading>Contact Us</Heading>
        <p style={{ color: "#666", marginBottom: "2rem" }}>
          Have questions or want to learn more? Send us a message and we'll get back to you soon.
        </p>
        
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="name">Name</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              required
            />
          </InputGroup>
          
          <InputGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              required
            />
          </InputGroup>
          
          <InputGroup>
            <Label htmlFor="message">Message</Label>
            <TextArea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Your message here..."
              required
            />
          </InputGroup>
          
          <SubmitButton type="submit" disabled={isSubmitting}>
            <FaPaperPlane /> {isSubmitting ? "Sending..." : "Send Message"}
          </SubmitButton>
          
          {isSuccess && (
            <SuccessMessage>
              Message sent successfully! We'll get back to you soon.
            </SuccessMessage>
          )}
        </Form>
      </FormContainer>
      
      <ImageContainer>
        <ContactImage
          src="https://img.freepik.com/premium-vector/marketing-research-isolated-cartoon-vector-illustrations_107173-30314.jpg?w=1060"
          alt="Contact illustration"
        />
      </ImageContainer>
    </ContactContainer>
  );
};

export default ContactForm;